/**
 * Gestión de Transferencias Pendientes - Panel de Administración
 *
 * Este componente permite a los administradores:
 * - Ver todas las transferencias bancarias pendientes
 * - Validar y aprobar transferencias
 * - Completar el proceso de inscripción tras la validación
 * - Ver detalles de cada transferencia
 */

import { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import UnifiedDataTable from '../../components/admin/UnifiedDataTable';
import StandardModal from '../../components/admin/StandardModal';
import { toast } from 'react-toastify';
import paymentService from '../../services/paymentService';
import './TransferManagement.css';

const TransferManagement = () => {
  const navigate = useNavigate();

  // Estados principales
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [_error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Estados para modales
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [validating, setValidating] = useState(false);

  // Estados para filtros
  const [filters, setFilters] = useState({
    status: 'pending',
    page: 1,
    limit: 20
  });

  // Cargar datos iniciales
  useEffect(() => {
    loadTransfers();
    loadStats();
  }, [filters]);

  /**
   * Cargar transferencias pendientes
   */
  const loadTransfers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await paymentService.getAdminTransactions({
        ...filters,
        paymentMethod: 'bank_transfer'
      });

      if (response.success) {
        setTransfers(response.data.transactions || []);
      } else {
        throw new Error(response.message || 'Error al cargar transferencias');
      }
    } catch (err) {
      console.error('Error cargando transferencias:', err);
      setError(err.message);
      toast.error('Error al cargar las transferencias pendientes');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cargar estadísticas
   */
  const loadStats = async () => {
    try {
      const response = await paymentService.getTransferStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
    }
  };

  /**
   * Abrir modal de detalles
   */
  const openDetailsModal = (transfer) => {
    setSelectedTransfer(transfer);
    setShowDetailsModal(true);
  };

  /**
   * Formatear ID de transacción para mostrar
   */
  const formatTransactionId = (transactionId) => {
    if (!transactionId) return 'N/A';

    // Si es muy largo, mostrar solo los últimos 12 caracteres
    if (transactionId.length > 20) {
      return `...${transactionId.slice(-12)}`;
    }

    return transactionId;
  };

  /**
   * Abrir modal de validación
   */
  const openValidateModal = (transfer) => {
    console.log('🔄 Abriendo modal de validación para transferencia:', transfer);
    setSelectedTransfer(transfer);
    setShowValidateModal(true);
  };

  /**
   * Validar y completar transferencia
   */
  const handleValidateTransfer = async () => {
    if (!selectedTransfer) {
      console.error('❌ No hay transferencia seleccionada');
      return;
    }

    console.log('🔄 Iniciando validación de transferencia:', selectedTransfer);

    try {
      setValidating(true);

      // 1. Marcar transacción como completada
      console.log('📤 Enviando validación para transferencia:', {
        id: selectedTransfer._id,
        transactionId: selectedTransfer.transactionId,
        status: selectedTransfer.status,
        amount: selectedTransfer.amount,
        paymentMethod: selectedTransfer.paymentMethod
      });
      const validateResponse = await paymentService.validateTransfer(selectedTransfer._id);
      console.log('📥 Respuesta de validación:', validateResponse);

      if (!validateResponse.success) {
        throw new Error(validateResponse.message || 'Error al validar transferencia');
      }

      // ✅ El backend ya se encarga de procesar la inscripción automáticamente
      toast.success('Transferencia validada e inscripción procesada exitosamente');

      // Recargar datos
      await loadTransfers();
      await loadStats();

      // Cerrar modal
      setShowValidateModal(false);
      setSelectedTransfer(null);

    } catch (err) {
      console.error('❌ Error validando transferencia:', err);
      toast.error(err.message || 'Error al validar la transferencia');
    } finally {
      setValidating(false);
    }
  };

  /**
   * Rechazar transferencia
   */
  const handleRejectTransfer = async (transferId, reason) => {
    try {
      const response = await paymentService.rejectTransfer(transferId, reason);
      
      if (response.success) {
        toast.success('Transferencia rechazada');
        await loadTransfers();
        await loadStats();
      } else {
        throw new Error(response.message || 'Error al rechazar transferencia');
      }
    } catch (err) {
      console.error('Error rechazando transferencia:', err);
      toast.error(err.message || 'Error al rechazar la transferencia');
    }
  };

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'user',
      label: 'Usuario',
      render: (row) => (
        <div className="user-info">
          <div className="user-name">{row.user?.name || 'Usuario desconocido'}</div>
          <div className="user-email">{row.user?.email}</div>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Importe',
      render: (row) => (
        <div className="amount-info">
          <span className="amount">{row.amount}€</span>
          <span className="currency">{row.currency}</span>
        </div>
      )
    },
    {
      key: 'description',
      label: 'Descripción',
      render: (row) => (
        <div className="description-info">
          <div className="main-description">{row.description}</div>
          {row.inscripcionData && (
            <div className="inscription-details">
              Futbolista: {row.inscripcionData.futbolista_nombre || `ID: ${row.inscripcionData.futbolista_id}`}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => (
        <span className={`status-badge status-${row.status}`}>
          {row.status === 'pending' ? 'Pendiente' : 
           row.status === 'completed' ? 'Completada' : 
           row.status === 'failed' ? 'Rechazada' : row.status}
        </span>
      )
    },
    {
      key: 'transactionId',
      label: 'ID Transacción',
      render: (row) => (
        <div className="transaction-info">
          <span className="transaction-id-table" title={row.transactionId}>
            {formatTransactionId(row.transactionId)}
          </span>
        </div>
      )
    },
    {
      key: 'createdAt',
      label: 'Fecha',
      render: (row) => (
        <div className="date-info">
          <div className="created-date">
            {new Date(row.createdAt).toLocaleDateString('es-ES')}
          </div>
          <div className="created-time">
            {new Date(row.createdAt).toLocaleTimeString('es-ES')}
          </div>
        </div>
      )
    }
  ];

  // Acciones disponibles para cada fila
  const actions = [
    {
      key: 'details',
      icon: 'visibility',
      label: 'Ver detalles',
      type: 'default',
      onClick: openDetailsModal
    },
    {
      key: 'validate',
      icon: 'check_circle',
      label: 'Validar',
      type: 'success',
      onClick: openValidateModal,
      disabled: (row) => row.status !== 'pending'
    },
    {
      key: 'reject',
      icon: 'cancel',
      label: 'Rechazar',
      type: 'danger',
      onClick: (row) => handleRejectTransfer(row._id, 'Rechazada por administrador'),
      disabled: (row) => row.status !== 'pending'
    }
  ];

  return (
    <AdminLayout
      title="Gestión de Transferencias"
      subtitle="Valida y gestiona las transferencias bancarias pendientes"
    >
      {/* Botón de navegación */}
      <div className="mb-3">
        <Button variant="outline-secondary" onClick={() => navigate('/admin/matches')}>
          <FaArrowLeft className="me-2" />Volver a Torneos
        </Button>
      </div>

      {/* Estadísticas generales */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon pending">
              <span className="material-icons">hourglass_empty</span>
            </div>
            <div className="stat-info">
              <h3>Pendientes</h3>
              <p className="stat-value">{stats.pending || 0}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon completed">
              <span className="material-icons">check_circle</span>
            </div>
            <div className="stat-info">
              <h3>Validadas</h3>
              <p className="stat-value">{stats.completed || 0}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon amount">
              <span className="material-icons">euro</span>
            </div>
            <div className="stat-info">
              <h3>Importe Pendiente</h3>
              <p className="stat-value">{stats.pendingAmount || 0}€</p>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="admin-card">
        <div className="filters-section">
          <div className="filter-group">
            <label htmlFor="status-filter">Estado:</label>
            <select
              id="status-filter"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            >
              <option value="">Todos</option>
              <option value="pending">Pendientes</option>
              <option value="completed">Completadas</option>
              <option value="failed">Rechazadas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabla de transferencias */}
      <div className="admin-card">
        <UnifiedDataTable
          columns={columns}
          data={transfers}
          actions={actions}
          loading={loading}
          emptyMessage="No hay transferencias disponibles"
          pagination={true}
          searchable={true}
        />
      </div>

      {/* Modal de detalles */}
      <StandardModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Detalles de la Transferencia"
        size="large"
      >
        {selectedTransfer && (
          <div className="transfer-details">
            <div className="detail-section">
              <h4>Información General</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>ID Transacción:</label>
                  <span className="transaction-id" title={selectedTransfer.transactionId}>
                    {formatTransactionId(selectedTransfer.transactionId)}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Importe:</label>
                  <span>{selectedTransfer.amount}€</span>
                </div>
                <div className="detail-item">
                  <label>Estado:</label>
                  <span className={`status-badge status-${selectedTransfer.status}`}>
                    {selectedTransfer.status === 'pending' ? 'Pendiente' :
                     selectedTransfer.status === 'completed' ? 'Completada' :
                     selectedTransfer.status === 'failed' ? 'Rechazada' : selectedTransfer.status}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Método de Pago:</label>
                  <span>{selectedTransfer.paymentMethod === 'bank_transfer' ? 'Transferencia Bancaria' : selectedTransfer.paymentMethod}</span>
                </div>
                <div className="detail-item">
                  <label>Descripción:</label>
                  <span>{selectedTransfer.description}</span>
                </div>
                <div className="detail-item">
                  <label>Fecha:</label>
                  <span>{new Date(selectedTransfer.createdAt).toLocaleString('es-ES')}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Usuario</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Nombre:</label>
                  <span>{selectedTransfer.user?.name}</span>
                </div>
                <div className="detail-item">
                  <label>Email:</label>
                  <span>{selectedTransfer.user?.email}</span>
                </div>
              </div>
            </div>

            {selectedTransfer.inscripcionData && (
              <div className="detail-section">
                <h4>Datos de Inscripción</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Futbolista:</label>
                    <span>{selectedTransfer.inscripcionData.futbolista_nombre || `ID: ${selectedTransfer.inscripcionData.futbolista_id}`}</span>
                  </div>
                  <div className="detail-item">
                    <label>Paquete:</label>
                    <span>ID: {selectedTransfer.inscripcionData.paquete_id}</span>
                  </div>
                  <div className="detail-item">
                    <label>Torneo:</label>
                    <span>{selectedTransfer.inscripcionData.torneo_nombre || 'No especificado'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Sede:</label>
                    <span>{selectedTransfer.inscripcionData.sede_nombre || 'No especificada'}</span>
                  </div>
                  {selectedTransfer.inscripcionData.equipacion && (
                    <div className="detail-item">
                      <label>Equipación:</label>
                      <span>{selectedTransfer.inscripcionData.equipacion.incluir ? 'Sí' : 'No'}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </StandardModal>

      {/* Modal de validación */}
      <StandardModal
        isOpen={showValidateModal}
        onClose={() => setShowValidateModal(false)}
        title="Validar Transferencia"
        size="medium"
      >
        {selectedTransfer && (
          <div className="validate-transfer">
            <div className="validation-info">
              <p><strong>¿Confirmas que has recibido la transferencia?</strong></p>
              <div className="transfer-summary">
                <div className="summary-row">
                  <span className="label">Usuario:</span>
                  <span className="value">{selectedTransfer.user?.name}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Importe:</span>
                  <span className="value">{selectedTransfer.amount}€</span>
                </div>
                <div className="summary-row">
                  <span className="label">Futbolista:</span>
                  <span className="value">{selectedTransfer.inscripcionData?.futbolista_nombre}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Paquete:</span>
                  <span className="value">ID: {selectedTransfer.inscripcionData?.paquete_id}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Torneo:</span>
                  <span className="value">{selectedTransfer.inscripcionData?.torneo_nombre || 'No especificado'}</span>
                </div>
                <div className="summary-row">
                  <span className="label">ID Transacción:</span>
                  <span className="value transaction-id" title={selectedTransfer.transactionId}>
                    {formatTransactionId(selectedTransfer.transactionId)}
                  </span>
                </div>
              </div>
              <div className="validation-warning">
                <span className="material-icons">warning</span>
                <p>Al validar esta transferencia se completará automáticamente el proceso de inscripción y se añadirá el saldo correspondiente.</p>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowValidateModal(false)}
                disabled={validating}
              >
                Cancelar
              </button>
              <button
                className="btn btn-success"
                onClick={handleValidateTransfer}
                disabled={validating}
              >
                {validating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Validando...
                  </>
                ) : (
                  <>
                    <span className="material-icons">check_circle</span>
                    Validar Transferencia
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </StandardModal>
    </AdminLayout>
  );
};

export default TransferManagement;
